/**
 * i18n configuration (Issue #447)
 *
 * Uses react-i18next with HTTP backend for lazy-loading translation files.
 * Translation JSONs live in public/locales/{lang}/translation.json.
 * Language preference syncs with the existing tacit-preferred-language localStorage key.
 *
 * PostHog instrumentation (Issue #499): emits ui_language_changed events to support
 * RTL prioritisation. See loop closure comment at issue #499.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { getPreferredLanguage } from './lib/voice/languageConfig';
import { posthog } from './main';
import { captureLanguageChanged } from './lib/telemetry/languageChangedCapture';

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: getPreferredLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    backend: {
      loadPath: '/locales/{{lng}}/translation.json',
    },
    // Only load the 'translation' namespace
    ns: ['translation'],
    defaultNS: 'translation',
    // Suppress missing key warnings in production
    saveMissing: false,
    missingKeyHandler: false,
    react: {
      useSuspense: false, // Avoids Suspense boundary requirement
    },
  });

// Track the previous locale for ui_language_changed instrumentation
let previousLocale = i18n.language || 'en';

// Update <html lang="..."> when language changes, and emit PostHog event
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;

  // Emit PostHog event for UI language changes (Issue #499)
  captureLanguageChanged(posthog, lng, previousLocale);

  // Update previous locale for the next change
  previousLocale = lng;
});
// Set initial value
document.documentElement.lang = i18n.language || 'en';

export default i18n;
